/**
 * Plateforme multi evenement e.labriquerie.net, (c) Mikaël FOURNY.
 * src/i18n/fr.platinium.pilotage.ts
 *
 * Messages du lot P6 du chantier Platinium (cahier des charges
 * app/docs/CDC-PLATINIUM.md) : onglet Tableau de bord de l'entreprise,
 * écran /admin/platinium (réglages, demandes, suivi interne, courriel
 * « Complétez votre profil », position des communes), filtre de l'écran
 * Exposants, section Platinium de la fiche complète.
 *
 * Branché par la vague 0 (fusionné dans fr.ts, inscrit au garde-fou des
 * sous-catalogues). Les textes communs (statut, garde, accord) sont dans
 * fr.platinium.socle.ts et sont réutilisés, jamais redéfinis.
 *
 * Règles : préfixe « platinium.pilotage. » (plus le libellé de navigation
 * adminNav.platinium) ; vouvoiement, apostrophes typographiques, aucun tiret
 * cadratin, aucun nom, lieu ni type d'événement écrit en dur
 * ({eventTypeDef}). Chiffres de l'événement : strictement internes, jamais
 * dans un texte public.
 *
 * Mikael - chef de projet - 2026-10-14
 */
export const messagesPlatiniumPilotageFr: Record<string, string> = {
  // ── Navigation et commun ───────────────────────────────────────────────
  'adminNav.platinium': 'Platinium : réglages et suivi',
  'platinium.pilotage.titre': 'Platinium : réglages et suivi',
  'platinium.pilotage.intro':
    'Le Platinium prolonge le recrutement après {eventTypeDef} : réglages, demandes des entreprises, suivi interne et courriel aux candidats.',
  'platinium.pilotage.chargement': 'Chargement du Platinium…',
  'platinium.pilotage.erreur.chargement': 'Le Platinium n’a pas pu être chargé. Réessayez dans un instant.',
  'platinium.pilotage.erreur': 'L’action n’a pas abouti. Réessayez.',
  'platinium.pilotage.annuler': 'Annuler',
  'platinium.pilotage.inconnue': 'inconnue',

  // ── État du module et fenêtre réelle ──────────────────────────────────
  'platinium.pilotage.etat.titre': 'État et dates d’accès',
  'platinium.pilotage.etat.actif': 'Le Platinium est activé.',
  'platinium.pilotage.etat.eteint': 'Le Platinium est désactivé. Activez-le dans les réglages ci-dessous.',
  'platinium.pilotage.etat.type':
    'Le Platinium n’est proposé que pour un événement de recrutement : il ne peut pas être activé sur cet événement.',
  'platinium.pilotage.fenetre.ouverture': 'Ouverture de l’accès : {date}, à la fin de {eventTypeDef}.',
  'platinium.pilotage.fenetre.fermeture': 'Dernier jour d’accès : {date}.',
  'platinium.pilotage.fenetre.motif.duree': '(fixé par la durée du Platinium)',
  'platinium.pilotage.fenetre.motif.conservation': '(fixé par la conservation des CV, qui l’emporte)',
  'platinium.pilotage.fenetre.detail':
    'Par la durée : {duree}. Par la conservation des CV ({retention} jours après {eventTypeDef}) : {conservation}. La plus proche des deux dates est retenue.',
  'platinium.pilotage.fenetre.etat.avant': 'L’accès n’est pas encore ouvert.',
  'platinium.pilotage.fenetre.etat.ouverte':
    '{jours, plural, =0 {L’accès est ouvert, dernier jour aujourd’hui.} one {L’accès est ouvert, encore # jour.} other {L’accès est ouvert, encore # jours.}}',
  'platinium.pilotage.fenetre.etat.fermee': 'L’accès est fermé.',
  'platinium.pilotage.fenetre.etat.indeterminee':
    'Les dates de l’accès ne sont pas connues : renseignez la fin de {eventTypeDef} et la conservation des CV. L’accès reste fermé.',
  'platinium.pilotage.plafond': 'Entreprises Platinium actives : {actifs} sur {plafond}.',
  'platinium.pilotage.plafond.sans': 'Entreprises Platinium actives : {actifs}, sans plafond.',

  // ── Demandes et entreprises ───────────────────────────────────────────
  'platinium.pilotage.demandes.titre': 'Demandes en attente ({n})',
  'platinium.pilotage.demandes.aucune': 'Aucune demande en attente.',
  'platinium.pilotage.demandes.droit':
    'Valider, refuser ou retirer le Platinium demande le droit « Accorder le Platinium », attribué dans Contrôle d’accès.',
  'platinium.pilotage.decision.ok.activate': 'L’entreprise est Platinium.',
  'platinium.pilotage.decision.ok.refuse': 'La demande est refusée.',
  'platinium.pilotage.decision.ok.retire': 'Le Platinium est retiré à cette entreprise. Rien n’est supprimé.',
  'platinium.pilotage.decision.erreur': 'La décision n’a pas abouti. Le plafond est peut-être atteint.',
  'platinium.pilotage.entreprises.titre': 'Entreprises Platinium',
  'platinium.pilotage.entreprises.aucune': 'Aucune entreprise Platinium pour le moment.',
  'platinium.pilotage.entreprises.col.entreprise': 'Entreprise',
  'platinium.pilotage.entreprises.col.statut': 'Statut',
  'platinium.pilotage.entreprises.col.action': 'Décision',

  // ── Suivi interne ─────────────────────────────────────────────────────
  'platinium.pilotage.suivi.titre': 'Entreprises et suivi interne',
  'platinium.pilotage.suivi.interne':
    'Chiffres strictement internes : ils ne sont jamais affichés au public ni communiqués sans l’accord de l’organisation.',
  'platinium.pilotage.suivi.total.entreprises_actives': 'Entreprises actives',
  'platinium.pilotage.suivi.total.demandes': 'Demandes en attente',
  'platinium.pilotage.suivi.total.vivier': 'Candidats suivis',
  'platinium.pilotage.suivi.total.recherches': 'Recherches enregistrées',
  'platinium.pilotage.suivi.total.alertes': 'Alertes envoyées',
  'platinium.pilotage.suivi.total.consultations': 'Consultations de CV',
  'platinium.pilotage.suivi.total.messages': 'Messages envoyés',
  'platinium.pilotage.suivi.total.entretiens_proposes': 'Entretiens proposés',
  'platinium.pilotage.suivi.total.entretiens_menes': 'Entretiens menés',
  'platinium.pilotage.suivi.total.recrutements': 'Recrutements déclarés',
  'platinium.pilotage.suivi.export': 'Exporter le suivi (tableur, usage interne)',

  // ── Réglages ──────────────────────────────────────────────────────────
  'platinium.pilotage.reglages.titre': 'Réglages',
  'platinium.pilotage.reglages.groupe.activation': 'Activation',
  'platinium.pilotage.reglages.groupe.durees': 'Durées et délais',
  'platinium.pilotage.reglages.groupe.vivier': 'Étapes du vivier et grille d’évaluation',
  'platinium.pilotage.reglages.groupe.envois': 'Envois',
  'platinium.pilotage.reglages.groupe.debits': 'Plafonds par heure et par compte',
  'platinium.pilotage.reglages.debits.aide':
    'Protection contre les abus : au-delà, le serveur demande de patienter. Le plafond des contacts compte ensemble les messages envoyés et les propositions d’entretien.',
  'platinium.pilotage.reglages.enregistrer':
    '{n, plural, =0 {Enregistrer} one {Enregistrer # modification} other {Enregistrer # modifications}}',
  'platinium.pilotage.reglages.annuler': 'Annuler les modifications',
  'platinium.pilotage.reglages.ok': 'Réglages enregistrés.',
  'platinium.pilotage.reglages.refus': 'Certains réglages n’ont pas été enregistrés :',
  'platinium.pilotage.reglages.raison.cle_inconnue': 'réglage inconnu',
  'platinium.pilotage.reglages.raison.cle_absente': 'réglage absent de la base (migration à appliquer)',
  'platinium.pilotage.reglages.raison.valeur_invalide': 'valeur invalide',
  'platinium.pilotage.reglages.raison.hors_bornes': 'valeur hors des limites permises',
  'platinium.pilotage.reglages.raison.json_invalide': 'liste illisible (voir l’aide sous le champ)',
  'platinium.pilotage.reglages.raison.type_evenement': 'activation impossible sur cet événement',
  'platinium.pilotage.reglages.raison.db_error': 'erreur d’enregistrement',
  'platinium.pilotage.reglage.etapes.aide':
    'Liste au format JSON, dans l’ordre : [{"cle":"a_lire","libelle":"À lire","terminale":false}, …]. La clé : minuscules, chiffres et soulignés.',
  'platinium.pilotage.reglage.criteres.aide':
    'Liste au format JSON : [{"cle":"competences","libelle":"Compétences"}, …]. Chaque critère est noté de 1 à 5.',
  'platinium.pilotage.reglage.enabled': 'Activer le Platinium',
  'platinium.pilotage.reglage.request_enabled': 'Les exposants peuvent demander le Platinium',
  'platinium.pilotage.reglage.alertes_enabled': 'Alertes de nouveaux CV',
  'platinium.pilotage.reglage.duree_jours': 'Durée de l’accès après la fin (jours)',
  'platinium.pilotage.reglage.purge_vivier_jours': 'Purge du vivier après la fermeture (jours)',
  'platinium.pilotage.reglage.rappels_fermeture_jours': 'Rappels avant la fermeture (jours, séparés par des virgules)',
  'platinium.pilotage.reglage.reponse_heures': 'Délai de réponse à un entretien (heures)',
  'platinium.pilotage.reglage.rappel_minutes': 'Rappel avant un entretien (minutes)',
  'platinium.pilotage.reglage.max_exhibitors': 'Plafond d’entreprises Platinium (0 = sans plafond)',
  'platinium.pilotage.reglage.etapes_defaut': 'Étapes du vivier proposées par défaut',
  'platinium.pilotage.reglage.criteres_evaluation': 'Critères de la grille d’évaluation',
  'platinium.pilotage.reglage.alertes_max_cv': 'CV listés au plus dans une alerte',
  'platinium.pilotage.reglage.completer_lot': 'Courriels « Complétez votre profil » par lot',
  'platinium.pilotage.reglage.geoloc_lot': 'Communes géolocalisées par passage',
  'platinium.pilotage.reglage.debit_contacts': 'Contacts : messages et propositions d’entretien',
  'platinium.pilotage.reglage.debit_vivier_ecritures': 'Vivier : modifications',
  'platinium.pilotage.reglage.debit_vivier_lectures': 'Vivier : lectures',
  'platinium.pilotage.reglage.debit_entretiens_ecritures': 'Entretiens : actions',
  'platinium.pilotage.reglage.debit_entretiens_lectures': 'Entretiens : lectures',
  'platinium.pilotage.reglage.debit_messages_ecritures': 'Messages : actions',
  'platinium.pilotage.reglage.debit_messages_lectures': 'Messages : lectures',
  'platinium.pilotage.reglage.debit_candidat_ecritures': 'Candidat : réponses aux entretiens',
  'platinium.pilotage.reglage.debit_candidat_lectures': 'Candidat : lectures de ses entretiens',
  'platinium.pilotage.reglage.debit_accord': 'Candidat : changements de l’accord',
  'platinium.pilotage.reglage.debit_demande': 'Exposant : demandes de Platinium',
  'platinium.pilotage.reglage.debit_admin': 'Organisation : actions sur le Platinium',
  'platinium.pilotage.reglage.debit_tableau': 'Tableau de bord : lectures',

  // ── Courriel « Complétez votre profil » ───────────────────────────────
  'platinium.pilotage.completer.titre': 'Courriel « Complétez votre profil »',
  'platinium.pilotage.completer.aide':
    'Envoyé à la main, jamais automatiquement : aux personnes qui ont donné l’accord Platinium, ont un CV actif et à qui il manque au moins un des nouveaux champs du profil. Une personne ne le reçoit qu’une fois. Affichez d’abord l’aperçu.',
  'platinium.pilotage.completer.apercu': 'Aperçu',
  'platinium.pilotage.completer.destinataires':
    '{n, plural, =0 {Aucune personne ne recevrait ce courriel} one {# personne recevrait ce courriel} other {# personnes recevraient ce courriel}} ({deja, plural, =0 {aucun envoi déjà fait} one {# déjà envoyé} other {# déjà envoyés}}).',
  'platinium.pilotage.completer.sujet': 'Objet :',
  'platinium.pilotage.completer.cadre': 'Aperçu du courriel, avec un prénom d’exemple',
  'platinium.pilotage.completer.envoyer': 'Envoyer…',
  'platinium.pilotage.completer.droit': 'L’envoi demande le droit « Accorder le Platinium », attribué dans Contrôle d’accès.',
  'platinium.pilotage.completer.confirmer.titre':
    '{n, plural, one {Confirmer l’envoi à # personne ?} other {Confirmer l’envoi à # personnes ?}}',
  'platinium.pilotage.completer.confirmer.case':
    'J’ai relu le courriel et je confirme son envoi à {n, plural, one {# personne} other {# personnes}}.',
  'platinium.pilotage.completer.confirmer.bouton': 'Confirmer l’envoi',
  'platinium.pilotage.completer.bilan':
    '{envoyes, plural, =0 {Aucun courriel envoyé} one {# courriel envoyé} other {# courriels envoyés}}{echecs, plural, =0 {.} one {, # échec.} other {, # échecs.}}',
  'platinium.pilotage.completer.refus.jeton_invalide':
    'Le courriel a changé depuis l’aperçu. Affichez de nouveau l’aperçu avant d’envoyer.',
  'platinium.pilotage.completer.refus.module_indisponible': 'Le Platinium est désactivé : le courriel ne peut pas partir.',
  'platinium.pilotage.completer.refus.fenetre_fermee':
    'La date de fermeture de l’accès n’est pas connue ou l’accès est fermé : le courriel ne peut pas partir.',
  'platinium.pilotage.completer.refus.platinium_grant_forbidden': 'Vous n’avez pas le droit d’envoyer ce courriel.',

  // ── Position des communes ─────────────────────────────────────────────
  'platinium.pilotage.communes.titre': 'Position des communes',
  'platinium.pilotage.communes.aide':
    'Place sur la carte les personnes qui ont une commune sans position, pour la recherche par distance. Au plus {lot} communes par clic.',
  'platinium.pilotage.communes.bouton': 'Recalculer la position des communes',
  'platinium.pilotage.communes.suite': 'Continuer avec les communes suivantes',
  'platinium.pilotage.communes.rien':
    'Rien à placer pour l’instant : aucune personne n’a indiqué une commune qui ne soit pas déjà sur la carte. Les communes se placent d’elles-mêmes quand les candidats complètent leur profil.',
  'platinium.pilotage.communes.bilan':
    '{communes, plural, =0 {Aucune commune traitée} one {# commune traitée} other {# communes traitées}}, {personnes, plural, =0 {aucune personne placée} one {# personne placée} other {# personnes placées}}, {introuvables, plural, =0 {aucune commune introuvable} one {# commune introuvable} other {# communes introuvables}}. {restantes, plural, =0 {Plus aucune commune à traiter.} one {# commune reste à traiter.} other {# communes restent à traiter.}}',

  // ── Filtre de l'écran Exposants ───────────────────────────────────────
  'platinium.pilotage.filtre': 'Platinium',
  'platinium.pilotage.filtre.tous': 'Tous',
  'platinium.pilotage.filtre.actif': 'Platinium',
  'platinium.pilotage.filtre.demande': 'Demande en attente',
  'platinium.pilotage.filtre.non': 'Non Platinium',

  // ── Onglet Tableau de bord de l'entreprise ────────────────────────────
  'platinium.pilotage.tableau.chargement': 'Chargement de votre tableau de bord…',
  'platinium.pilotage.tableau.erreur': 'Votre tableau de bord n’a pas pu être chargé. Réessayez dans un instant.',
  'platinium.pilotage.tableau.acces': 'Votre accès',
  'platinium.pilotage.tableau.rappelExport':
    'L’accès ferme bientôt : pensez à exporter votre vivier (onglet Mon vivier, bouton Exporter). Le vivier, les notes, les messages et les entretiens seront supprimés {jours, plural, =0 {à la fermeture} one {# jour après la fermeture} other {# jours après la fermeture}}.',
  'platinium.pilotage.tableau.entonnoir': 'Du premier regard au recrutement',
  'platinium.pilotage.tableau.entonnoir.aide':
    'Chaque candidat compte une fois par étape atteinte. Les candidats qui ont retiré leur accord n’y figurent plus.',
  'platinium.pilotage.tableau.marche.vus': 'CV vus',
  'platinium.pilotage.tableau.marche.coups_de_coeur': 'Coups de cœur',
  'platinium.pilotage.tableau.marche.contactes': 'Contactés',
  'platinium.pilotage.tableau.marche.entretiens': 'Entretiens',
  'platinium.pilotage.tableau.marche.propositions': 'Propositions faites',
  'platinium.pilotage.tableau.marche.recrutes': 'Recrutés',
  'platinium.pilotage.tableau.delais': 'Temps moyen passé dans chaque étape',
  'platinium.pilotage.tableau.delais.aucun': 'Pas encore assez de mouvements dans votre vivier pour le calculer.',
  'platinium.pilotage.tableau.delais.col.etape': 'Étape',
  'platinium.pilotage.tableau.delais.col.jours': 'Temps moyen',
  'platinium.pilotage.tableau.delais.col.n': 'Candidats',
  'platinium.pilotage.tableau.delais.jours': '{n, plural, one {# jour} other {# jours}}',
  'platinium.pilotage.tableau.equipe': 'Activité de votre équipe',
  'platinium.pilotage.tableau.equipe.mere': '(compte principal)',
  'platinium.pilotage.tableau.equipe.col.nom': 'Recruteur',
  'platinium.pilotage.tableau.equipe.col.ajouts': 'Ajouts au vivier',
  'platinium.pilotage.tableau.equipe.col.messages': 'Messages',
  'platinium.pilotage.tableau.equipe.col.entretiens': 'Entretiens',
  'platinium.pilotage.tableau.equipe.col.notes': 'Notes',
  'platinium.pilotage.tableau.equipe.col.actions': 'Actions',
  'platinium.pilotage.tableau.equipe.col.derniere': 'Dernière action',
  'platinium.pilotage.tableau.retard': 'Tâches en retard ({n})',
  'platinium.pilotage.tableau.retard.aucune': 'Aucune tâche en retard.',
  'platinium.pilotage.tableau.retard.ligne': 'pour {candidat}, échéance le {date}, confiée à {responsable}',
  'platinium.pilotage.tableau.retard.ligneSans': 'pour {candidat}, échéance le {date}',

  // ── Fiche complète d'une personne ─────────────────────────────────────
  'platinium.pilotage.fiche.titre': 'Platinium',
  'platinium.pilotage.fiche.moduleEteint': 'Le Platinium est désactivé sur cet événement ; ces informations sont conservées.',
  'platinium.pilotage.fiche.accord': 'Accord Platinium',
  'platinium.pilotage.fiche.accord.oui': 'Donné le {date}, {source}',
  'platinium.pilotage.fiche.accord.non': 'Non donné',
  'platinium.pilotage.fiche.mineur': 'Mineur',
  'platinium.pilotage.fiche.recherche': 'Recherche un emploi',
  'platinium.pilotage.fiche.recherche.oui': 'Oui',
  'platinium.pilotage.fiche.recherche.non': 'Non, a indiqué avoir trouvé un emploi',
  'platinium.pilotage.fiche.suivi': 'Suivi par',
  'platinium.pilotage.fiche.suivi.n':
    '{n, plural, =0 {aucune entreprise} one {# entreprise, dans son vivier} other {# entreprises, dans leur vivier}}',
  'platinium.pilotage.fiche.completer': 'Courriel « Complétez votre profil »',
  'platinium.pilotage.fiche.completer.le': 'Envoyé le {date}',
  'platinium.pilotage.fiche.completer.jamais': 'Jamais envoyé',
  'platinium.pilotage.fiche.historique': 'Historique des accords',
  'platinium.pilotage.fiche.historique.col.quand': 'Date',
  'platinium.pilotage.fiche.historique.col.accord': 'Accord',
  'platinium.pilotage.fiche.historique.col.valeur': 'Choix',
  'platinium.pilotage.fiche.historique.col.source': 'Où',
  'platinium.pilotage.fiche.type.premium': 'Entreprises premium',
  'platinium.pilotage.fiche.type.toutes': 'Toutes les entreprises',
  'platinium.pilotage.fiche.type.platinium': 'Platinium',
  'platinium.pilotage.fiche.valeur.accorde': 'Accordé',
  'platinium.pilotage.fiche.valeur.retire': 'Retiré',
  'platinium.pilotage.fiche.valeur.scanned': 'Stands scannés seulement',
  'platinium.pilotage.fiche.valeur.premium': 'Entreprises premium',
  'platinium.pilotage.fiche.valeur.all': 'Toutes les entreprises',
  'platinium.pilotage.fiche.consultations': 'Entreprises qui ont consulté le CV',
  'platinium.pilotage.fiche.consultations.col.entreprise': 'Entreprise',
  'platinium.pilotage.fiche.consultations.col.vues': 'Consultations',
  'platinium.pilotage.fiche.consultations.col.contacts': 'Coordonnées vues',
  'platinium.pilotage.fiche.consultations.col.derniere': 'Dernière fois',
  'platinium.pilotage.fiche.entretiens': 'Entretiens Platinium',
  'platinium.pilotage.fiche.entretiens.col.quand': 'Date',
  'platinium.pilotage.fiche.entretiens.col.entreprise': 'Entreprise',
  'platinium.pilotage.fiche.entretiens.col.candidat': 'Candidat',
  'platinium.pilotage.fiche.entretiens.col.recruteur': 'Recruteur',
  'platinium.pilotage.fiche.entretiens.col.statut': 'Statut',
  'platinium.pilotage.fiche.entretiens.col.evaluation': 'Évaluation',
  'platinium.pilotage.fiche.sansEvaluation':
    'L’évaluation d’un entretien reste interne à l’entreprise : elle n’apparaît pas sur la fiche du candidat.',
  'platinium.pilotage.fiche.evaluationInterne': 'Évaluations internes à l’entreprise, jamais montrées au candidat.',
  'platinium.pilotage.fiche.evaluation': '{note} sur 5, {decision}',
  'platinium.pilotage.fiche.decision.a_revoir': 'à revoir',
  'platinium.pilotage.fiche.decision.proposition': 'proposition',
  'platinium.pilotage.fiche.decision.ne_pas_retenir': 'ne pas retenir',
  'platinium.pilotage.fiche.decision.absent': 'absent',
  'platinium.pilotage.fiche.statut': 'Statut Platinium',
  'platinium.pilotage.fiche.valideLe': '(depuis le {date})',
  'platinium.pilotage.fiche.vivier': 'Vivier',
  'platinium.pilotage.fiche.vivier.vide': 'vide',
  'platinium.pilotage.fiche.recherches': 'Recherches enregistrées',
  'platinium.pilotage.fiche.recrutements': 'Recrutements déclarés',
  'platinium.pilotage.fiche.statut.propose': 'Proposé',
  'platinium.pilotage.fiche.statut.accepte': 'Accepté',
  'platinium.pilotage.fiche.statut.refuse': 'Refusé',
  'platinium.pilotage.fiche.statut.contre_propose': 'Autre moment proposé',
  'platinium.pilotage.fiche.statut.expire': 'Expiré',
  'platinium.pilotage.fiche.statut.annule': 'Annulé',
  'platinium.pilotage.fiche.statut.termine': 'Mené',
  'platinium.pilotage.fiche.statut.absent': 'Absent',
};
